export type StepType = "lesson" | "exercise" | "quiz";

export interface CheatEntry {
  cmd: string;
  desc: string;
}

export interface ProgrammeModule {
  id: string;
  order: number;
  title: string;
  duration: number;
  summary: string;
  available: boolean;
  steps: { id: string; type: StepType }[];
  cheatsheet: CheatEntry[];
}

export interface Programme {
  title: string;
  subtitle: string;
  description: string;
  progress: Record<string, string[]>;
  modules: ProgrammeModule[];
}

export interface LessonStep {
  type: "lesson";
  id: string;
  title: string;
  html: string;
}

export interface ExerciseStep {
  type: "exercise";
  id: string;
  title: string;
  html: string;
  hintCount: number;
}

export interface QuizStep {
  type: "quiz";
  id: string;
  title: string;
  questions: { question: string; choices: string[]; answer: number; explanation: string }[];
}

export type Step = LessonStep | ExerciseStep | QuizStep;

export interface ModuleDetail {
  id: string;
  order: number;
  title: string;
  duration: number;
  summary: string;
  hasEnvironment: boolean;
  cheatsheet: CheatEntry[];
  steps: Step[];
}

export class ApiError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method,
    credentials: "same-origin",
    headers: body === undefined ? {} : { "content-type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError(res.status, data.error ?? `Erreur ${res.status}`);
  return data as T;
}

export const api = {
  programme: () => request<Programme>("GET", "/api/programme"),
  module: (id: string) => request<{ module: ModuleDetail; completed: string[] }>("GET", `/api/modules/${id}`),
  prepareEnvironment: (id: string) => request<{ status: string }>("POST", `/api/modules/${id}/environment`),
  resetEnvironment: (id: string) => request<{ status: string }>("DELETE", `/api/modules/${id}/environment`),
  check: (id: string, step: string) =>
    request<{ ok: boolean; message: string }>("POST", `/api/modules/${id}/steps/${step}/check`),
  complete: (id: string, step: string) => request<{ ok: boolean }>("POST", `/api/modules/${id}/steps/${step}/complete`),
  hint: (id: string, step: string, index: number) =>
    request<{ html: string }>("GET", `/api/modules/${id}/steps/${step}/hints/${index}`),
  notes: () => request<{ content: string; updatedAt: number | null }>("GET", "/api/notes"),
  saveNotes: (content: string) => request<{ ok: boolean }>("PUT", "/api/notes", { content }),
};
